/**
 * Opening hours, shared by web and mobile. Stored as the API expects:
 * { mon: [["08:00", "12:30"], ["14:00", "17:30"]], sat: [["09:00", "13:00"]] } — a missing day is closed.
 * Edited as text: "08:00-12:30, 14:00-17:30".
 */

export const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
export type Weekday = (typeof WEEKDAYS)[number];
export type TimeRange = [string, string];
export type OpeningHours = Partial<Record<Weekday, TimeRange[]>>;

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

function normaliseTime(raw: string): string | null {
  const match = /^(\d{1,2})(?:[:h](\d{2})?)?$/.exec(raw.trim().toLowerCase());
  const hour = match?.[1];
  if (hour === undefined) return null;
  const time = `${hour.padStart(2, "0")}:${match?.[2] ?? "00"}`;
  return TIME.test(time) ? time : null;
}

/** "08:00-12:30, 14:00-17:30" → [["08:00","12:30"],["14:00","17:30"]]; empty → []; invalid → null. */
export function parseRanges(text: string): TimeRange[] | null {
  const trimmed = text.trim();
  if (!trimmed) return [];
  const ranges: TimeRange[] = [];
  for (const part of trimmed.split(/[,;]/)) {
    const [start, end, extra] = part.split(/[-–]/).map((t) => normaliseTime(t));
    if (!start || !end || extra !== undefined || start >= end) return null;
    const previous = ranges[ranges.length - 1];
    if (previous && start < previous[1]) return null;
    ranges.push([start, end]);
  }
  return ranges;
}

export function formatRanges(ranges: TimeRange[] | undefined): string {
  return (ranges ?? []).map(([start, end]) => `${start}-${end}`).join(", ");
}

/** Text per day → API value (closed days are left out). Returns the invalid days, if any. */
export function buildOpeningHours(texts: Partial<Record<Weekday, string>>): {
  hours: OpeningHours;
  invalid: Weekday[];
} {
  const hours: OpeningHours = {};
  const invalid: Weekday[] = [];
  for (const day of WEEKDAYS) {
    const ranges = parseRanges(texts[day] ?? "");
    if (ranges === null) invalid.push(day);
    else if (ranges.length) hours[day] = ranges;
  }
  return { hours, invalid };
}
